// Non-component helper (see apps/web/CLAUDE.md — .tsx files export components
// only). Builds the country/dial-code option list backing CountryPhoneField
// from country-telephone-data, which ships no ISO2 → flag asset of its own —
// flags are rendered as plain <img> from flagcdn.com (see getFlagImageUrl).
import { allCountries } from 'country-telephone-data';
import { getCountryForTimezone } from 'countries-and-timezones';

export type CountryPhoneOption = {
  /** Dial code, always "+"-prefixed, e.g. "+91". */
  value: string;
  /** Country name, e.g. "India". */
  label: string;
  /** Search haystack — country name + dial code, e.g. "India +91". */
  labelText: string;
  /** ISO 3166-1 alpha-2, uppercase, e.g. "IN". */
  iso2: string;
};

// Several countries share a dial code (+1 covers the US, Canada, and the rest
// of the NANP) — India and the US are pinned first as the two most common
// signups for this product.
const PINNED_DIAL_CODES = ['+91', '+1'];

function normalizeDialCode(rawDialCode: string): string {
  return rawDialCode.startsWith('+') ? rawDialCode : `+${rawDialCode}`;
}

function cleanCountryName(name: string): string {
  // country-telephone-data suffixes some names with a parenthetical local
  // name, e.g. "Belarus (Беларусь)" — drop it for display/search.
  const idx = name.indexOf(' (');
  return idx > 0 ? name.slice(0, idx).trim() : name;
}

function buildOptions(): CountryPhoneOption[] {
  const options = allCountries.map((country): CountryPhoneOption => {
    const dialCode = normalizeDialCode(country.dialCode);
    const name = cleanCountryName(country.name);
    return {
      value: dialCode,
      label: name,
      labelText: `${name} ${dialCode}`,
      iso2: country.iso2.toUpperCase(),
    };
  });

  options.sort((a, b) => {
    const aPinned = PINNED_DIAL_CODES.indexOf(a.value);
    const bPinned = PINNED_DIAL_CODES.indexOf(b.value);
    if (aPinned !== -1 || bPinned !== -1) {
      if (aPinned !== -1 && bPinned !== -1) return aPinned - bPinned;
      return aPinned !== -1 ? -1 : 1;
    }
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });

  return options;
}

// Computed once per module load — the country list is static for the life of
// the page, same as TimezoneSelect's `buildOptions(getSupportedTimezones())`.
const COUNTRY_PHONE_OPTIONS = buildOptions();

export function getCountryPhoneOptions(): CountryPhoneOption[] {
  return COUNTRY_PHONE_OPTIONS;
}

/** All countries sharing a dial code, e.g. "+1" → US, Canada, and NANP members. */
export function getCountriesByDialCode(dialCode: string): CountryPhoneOption[] {
  const normalized = normalizeDialCode(dialCode);
  return COUNTRY_PHONE_OPTIONS.filter((option) => option.value === normalized);
}

export function findCountryByIso2(iso2: string): CountryPhoneOption | undefined {
  const upper = iso2.toUpperCase();
  return COUNTRY_PHONE_OPTIONS.find((option) => option.iso2 === upper);
}

export function getFlagImageUrl(iso2: string): string {
  return `https://flagcdn.com/w40/${iso2.toLowerCase()}.png`;
}

const FALLBACK_DEFAULT_COUNTRY = { dialCode: '+1', iso2: 'US' } as const;

/**
 * Best-effort default country from the browser's IANA timezone (e.g.
 * "Asia/Kolkata" → "IN"), same Intl-based auto-detect source as
 * TimezoneSelect's own `Intl.supportedValuesOf('timeZone')` usage. The
 * timezone is a sharper signal than the locale's region subtag — a browser
 * set to "en-US" on a machine whose OS timezone is "Asia/Kolkata" is almost
 * always a user physically in India with English as their UI language, and
 * the locale region would default them to +1/US instead. Falls back to the
 * locale region, then to +1/US, when the timezone can't be resolved to a
 * single country (e.g. "Etc/UTC") or isn't in our list — there is no
 * IP-based signal available client-side (unlike the signup timezone, which
 * the backend resolves from the request IP). This is only ever the
 * *initial* value — the picker stays fully user-editable afterward.
 */
export function detectDefaultCountry(): { dialCode: string; iso2: string } {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = getCountryForTimezone(timeZone);
    const byTimezone = country ? findCountryByIso2(country.id) : undefined;
    if (byTimezone) return { dialCode: byTimezone.value, iso2: byTimezone.iso2 };

    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const region = new Intl.Locale(locale).region;
    const byLocale = region ? findCountryByIso2(region) : undefined;
    return byLocale ? { dialCode: byLocale.value, iso2: byLocale.iso2 } : FALLBACK_DEFAULT_COUNTRY;
  } catch {
    return FALLBACK_DEFAULT_COUNTRY;
  }
}
